import React, { useContext, useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { RegisterWrapper } from './styled/SRegister';
import { Input, Button, Header, Select, ResultMessage } from '../../components';
import { Box, Flex } from '@rebass/grid';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { AuthenticationContext } from '../../providers/AuthenticationProvider';
import { GeoContext } from '../../providers/GeoProvider';
import { useTranslation } from "react-i18next";
import { ThemeContext } from 'styled-components';
import { UsersContext } from '../../providers/UsersProvider';

interface FormValues {
    customerNumber: string;
    companyName: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
    password: string;
    country: string;
}

interface SelectOption {
    label: string;
    value: string;
}

function mapValues(values: FormValues) {
    let result: any = {};
    result['zuserprereg_firstname'] = values.firstName;
    result['zuserprereg_lastname'] = values.lastName;
    result['zuserprereg_country_code'] = values.country;
    result['zuserprereg_email'] = values.emailAddress;
    result['password'] = values.password;
    result['zuserprereg_company_code'] = values.customerNumber;
    result['zuserprereg_company_name'] = values.companyName;
    result['zuserprereg_telno'] = values.phoneNumber;
    return result;
}

const Register = () => {

    const { t } = useTranslation();

    const atLeast2Digits = new RegExp(/(.*\d.*){2,}/);
    const atLeast2NonDigits = new RegExp(/(.*[^\d].*){2,}/);

    const RegisterSchema = Yup.object().shape({
        customerNumber: Yup.string()
            .max(10, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 10 }))
            .required(t("VALIDATION_REQUIRED")),
        companyName: Yup.string()
            .max(64, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 64 }))
            .required(t("VALIDATION_REQUIRED")),
        firstName: Yup.string()
            .max(30, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 30 }))
            .required(t("VALIDATION_REQUIRED")),
        lastName: Yup.string()
            .max(30, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 30 }))
            .required(t("VALIDATION_REQUIRED")),
        phoneNumber: Yup.string()
            .max(30, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 30 })),
        emailAddress: Yup.string()
            .max(60, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 60 }))
            .email(t("VALIDATION_EMAIL_FORMAT"))
            .required(t("VALIDATION_REQUIRED")),
        password: Yup.string()
            .min(8, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 8 }))
            .matches(atLeast2Digits, t("VALIDATION_MIN_LENGTH_DIGITS_PLACEHOLDER", { placeholder: 2 }))
            .matches(atLeast2NonDigits, t("VALIDATION_MIN_LENGTH_NON_DIGITS_PLACEHOLDER", { placeholder: 2 }))
            .max(256, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 256 }))
            .required(t("VALIDATION_REQUIRED")),
        repeatPassword: Yup.string()
            .min(6, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 6 }))
            .max(256, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 256 }))
            .required(t("VALIDATION_REQUIRED"))
            .oneOf([Yup.ref('password'), null], t("VALIDATION_PASSWORD_MATCH")),
        country: Yup.string()
            .required(t("VALIDATION_REQUIRED")).nullable()
    });

    const { brand } = useContext(ThemeContext);
    const [currentCountry, setCurrentCountry] = useState({ label: t("SELECT_PLACEHOLDER", { placeholder: t("COUNTRY") }), value: '' } as SelectOption);

    const { authenticated, register, isLoading } = useContext(AuthenticationContext);
    const { countries, getCountries } = useContext(GeoContext);
    const { checkCompanyCode } = useContext(UsersContext);

    const hasCountriesInCache = Object.keys(countries).length !== 0;

    const [successMessage, showSuccessMessage] = useState(false);
    const [duplicateEmailMessage, setDuplicateEmailMessage] = useState('');

    const [customerIdError, invalidateCustomerId] = useState<string>('');


    useEffect(() => {
        if (!hasCountriesInCache) {
            getCountries();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCountriesInCache]);

    if (authenticated) {
        return <Redirect to='/documents' />
    }

    const handleChange = (setFieldValue: Function) => {
        function update(...args: any[]) {
            const selected = args[0] as SelectOption;
            setCurrentCountry(selected);
            setFieldValue('country', selected.value);
        }
        return update;
    }

    const handleBlur = (setFieldTouched: Function) => {
        function update() {
            setFieldTouched('country', true);
        }
        return update;
    }

    return <React.Fragment>

        <Formik
            validationSchema={RegisterSchema}
            initialValues={{
                customerNumber: '',
                companyName: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                emailAddress: '',
                password: '',
                repeatPassword: '',
                country: ''
            }}
            onSubmit={async (values: FormValues) => {
                if (brand === 'bcd' && values.customerNumber.length > 8) {
                    values.customerNumber = values.customerNumber.replace(/^4019/, '')
                }

                const validCustomerId = await checkCompanyCode(values.country, values.customerNumber);
                if (!validCustomerId) {
                    invalidateCustomerId(t("VALIDATION_CUSTOMER_ID"));
                    return;
                }
                const payload = mapValues(values);
                register(payload).then(res => {
                    showSuccessMessage(true);
                }).catch(error => {
                    showSuccessMessage(false);
                    const contactMessage = brand === 'brenntag' ? t("CONTACT_US_AT_BRENNTAG") : t("CONTACT_US_AT_BCD");
                    setDuplicateEmailMessage(error.response.data['zuserprereg_email'] + ' ' + contactMessage);
                });
            }}
            render={({ touched, errors, setFieldValue, setFieldTouched }) => (
                <Box id="box-register" data-page-id="register">
                    <RegisterWrapper>
                        <Header>{t("REGISTRATION")}</Header>

                        {successMessage ? <ResultMessage id={'box-registration-success'} subtitle={t("REGISTER_DONE_TITLE")} body={t("REGISTER_DONE_BODY")} /> : <Form>
                            <Flex justifyContent='space-between' mt={30} >
                                <Box width='100%'>
                                    <Input required={true} touched={touched.customerNumber} error={errors.customerNumber || customerIdError} label={t("CUSTOMER_ID")} id="input-customer-number" name="customerNumber" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("CUSTOMER_ID") })} />
                                </Box>
                                <Box width={80}></Box>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.companyName} error={errors.companyName} label={t("COMPANY_NAME")} id="input-company-name" name="companyName" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("COMPANY_NAME") })} />
                                </Box>
                            </Flex>
                            <Flex justifyContent='space-between' mt={30}>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.firstName} error={errors.firstName} label={t("FIRST_NAME")} id="input-first-name" name="firstName" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("FIRST_NAME") })} />
                                </Box>
                                <Box width={80}></Box>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.lastName} error={errors.lastName} label={t("LAST_NAME")} id="input-last-name" name="lastName" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("LAST_NAME") })} />
                                </Box>
                            </Flex>
                            <Flex justifyContent='space-between' mt={30}>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.emailAddress} error={errors.emailAddress || duplicateEmailMessage} label={t("EMAIL_ADDRESS")} id="input-email-address" name="emailAddress" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("EMAIL_ADDRESS") })} />
                                </Box>
                                <Box width={80}></Box>
                                <Box width='100%'>
                                    <Input touched={touched.phoneNumber} error={errors.phoneNumber} label={t("PHONE_NUMBER")} id="input-phone-number" name="phoneNumber" type="text" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("PHONE_NUMBER") })} />
                                </Box>
                            </Flex>
                            <Flex justifyContent='space-between' mt={30}>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.password} error={errors.password} label={t("PASSWORD")} id="input-password" name="password" type="password" placeholder={t("ENTER_PLACEHOLDER", { placeholder: t("PASSWORD") })} />
                                </Box>
                                <Box width={80}></Box>
                                <Box width='100%'>
                                    <Input required={true} touched={touched.repeatPassword} error={errors.repeatPassword} label={t("REPEAT_PASSWORD")} id="input-repeat-password" name="repeatPassword" type="password" placeholder={t("REPEAT_YOUR_PASSWORD")} />
                                </Box>
                            </Flex>
                            <Flex mt={30}>
                                <Box width='100%'>
                                    <Select required={true} touched={touched.country} error={errors.country} name={t("COUNTRY")} onChange={handleChange(setFieldValue)} onBlur={handleBlur(setFieldTouched)} options={countries} value={currentCountry} id="input-country" label={t("CUSTOMER_OF_PLACEHOLDER", { placeholder: brand })} placeholder={t("SELECT_PLACEHOLDER", { placeholder: t("COUNTRY") })} />
                                </Box>
                            </Flex>

                            <Flex justifyContent='flex-end'>
                                <Box mt={30}>
                                    <Button id="button-register" kind='primary' type='submit' loading={isLoading}>{t("REGISTER")}</Button>
                                </Box>
                            </Flex>

                        </Form>}
                    </RegisterWrapper>
                </Box>
            )}
        />

    </React.Fragment>
}

export default Register;
